import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';

import { Typography } from '@zendesk/forethought-elements';
import { formatAbTestValue } from '../formatAbTestValue';
import { VARIANT_DEFAULT_NAMES } from 'src/services/agent-ab-test/types';
import { useFetchDatasetQuery } from 'src/services/insight-cards';
import { CustomEventChart, SeriesItem } from 'src/services/insight-cards/types';

interface AbTestMetricCardProps {
  chart: CustomEventChart;
  endTimestamp: number;
  startTimestamp: number;
}

function getPercentChange(
  variantValue: number,
  controlValue: number,
): number | null {
  if (controlValue === 0) {
    return null;
  }
  return ((variantValue - controlValue) / controlValue) * 100;
}

const AbTestMetricCard = ({
  chart,
  endTimestamp,
  startTimestamp,
}: AbTestMetricCardProps) => {
  const { palette } = useTheme();
  const { t } = useTranslation('agent-ab-test');

  const { data: dataset } = useFetchDatasetQuery({
    aggregation_type: chart.aggregation_type,
    channels: chart.channels,
    end_timestamp: endTimestamp,
    formula_text: chart.formula_text,
    group_by: chart.group_by,
    measurement_type: chart.measurement_type,
    properties: chart.properties,
    start_timestamp: startTimestamp,
  });

  const series: SeriesItem[] = !dataset
    ? []
    : dataset.is_grouped
      ? (dataset.series[0] ?? [])
      : dataset.series;

  const valueSuffix = dataset?.tooltip?.value_suffix;
  const isTrafficSplit = chart.name.toLowerCase().includes('traffic');
  const controlValue = series[0]?.data ?? 0;
  const isEmpty = dataset !== undefined && series.length === 0;

  return (
    <Box
      sx={{
        border: `1px solid ${palette.colors.slate[200]}`,
        borderRadius: '8px',
        padding: '16px 20px',
      }}
    >
      <Typography variant='font14Bold'>{chart.name}</Typography>
      {isEmpty ? (
        <Box sx={{ color: palette.colors.grey[400], marginTop: '12px' }}>
          <Typography variant='font32'>{t('metricCard.naValue')}</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: '24px',
            marginTop: '12px',
          }}
        >
          {series.map((item, index) => {
            const percentChange =
              !isTrafficSplit && index > 0
                ? getPercentChange(item.data, controlValue)
                : null;
            const isPositive = percentChange !== null && percentChange >= 0;
            const showBadge = percentChange !== null;
            return (
              <Box
                key={index}
                sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'center',
                  }}
                >
                  <Box component='span' sx={{ fontWeight: 700 }}>
                    <Typography variant='font24'>
                      {formatAbTestValue(
                        item.data,
                        chart.aggregation_type,
                        valueSuffix,
                      )}
                    </Typography>
                  </Box>
                  {showBadge && (
                    <Box
                      sx={{
                        bgcolor: isPositive
                          ? palette.colors.green[100]
                          : palette.colors.red[100],
                        borderRadius: '4px',
                        color: isPositive
                          ? palette.colors.green[500]
                          : palette.colors.red[500],
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '2px 6px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t(
                        isPositive
                          ? 'metricCard.percentChangeUp'
                          : 'metricCard.percentChangeDown',
                        { value: Math.round(Math.abs(percentChange)) },
                      )}
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    gap: '6px',
                    marginTop: '4px',
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: palette.chart[index],
                      borderRadius: '50%',
                      flexShrink: 0,
                      height: '8px',
                      width: '8px',
                    }}
                  />
                  <Typography
                    color={palette.text.secondary}
                    noWrap
                    variant='font12'
                  >
                    {item.label ||
                      VARIANT_DEFAULT_NAMES[index] ||
                      t('metricCard.variantFallback', { index })}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default AbTestMetricCard;
